import { formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  Camera,
  ClipboardList,
  Map as MapIcon,
  Plus,
  RefreshCw,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { useMemo, type JSX } from "react";
import { Link } from "react-router";

import { BreakdownCard } from "../components/analytics/BreakdownCard.js";
import {
  DistributionBars,
  recordToEntries,
} from "../components/analytics/DistributionBars.js";
import { KpiCard } from "../components/analytics/KpiCard.js";
import { OperationsHealthCard } from "../components/analytics/OperationsHealthCard.js";
import { IssueTriageList } from "../components/compliance/IssueTriageList.js";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert.js";
import { Button } from "../components/ui/button.js";
import { Skeleton } from "../components/ui/skeleton.js";
import {
  useAnalyticsIssuesQuery,
  useAnalyticsSummaryQuery,
  useAnalyticsTrendsQuery,
} from "../lib/api/analytics.js";
import { useIssuesQuery } from "../lib/api/issues.js";
import { cn } from "../lib/cn.js";
import { toErrorMessage } from "../lib/http.js";
import { useSelectedStore } from "../providers/selected-store-provider.js";
import { useAuth } from "../providers/auth-provider.js";

function getGreeting(date: Date): string {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function firstNameFromEmail(email: string | undefined | null): string {
  if (!email) return "there";
  const local = email.split("@")[0] ?? "";
  const head = local.split(/[._\-+]/)[0] ?? local;
  if (!head) return "there";
  return head.charAt(0).toUpperCase() + head.slice(1);
}

/** Match the current UTC rollup buckets. */
function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const TREND_DAYS = 30;
const TOP_ISSUES = 4;

const QUICK_ACTIONS: Array<{ to: string; label: string; icon: typeof Plus }> = [
  { to: "/scans/new", label: "New scan", icon: Plus },
  { to: "/products/onboard", label: "Onboard from scan", icon: Sparkles },
  { to: "/store-layout/map", label: "Open Active Map", icon: MapIcon },
  { to: "/analytics/trends", label: "Analytics trends", icon: TrendingUp },
];

export function DashboardPage(): JSX.Element {
  const { user } = useAuth();

  const { selectedStore, isLoading: storesLoading } = useSelectedStore();
  const scope = { storeId: selectedStore?.id };
  const queryOptions = { enabled: !storesLoading };
  const summaryQuery = useAnalyticsSummaryQuery(scope, queryOptions);
  const issuesQuery = useAnalyticsIssuesQuery(scope, queryOptions);

  // Detected gaps for the hero. 30 days, daily buckets — enough to show
  // direction of travel without the sparkline turning to noise.
  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - (TREND_DAYS - 1) * 24 * 60 * 60 * 1000);
    return { from: ymd(from), to: ymd(to) };
  }, []);
  const trendsQuery = useAnalyticsTrendsQuery({
    ...scope,
    from: range.from,
    to: range.to,
    bucket: "day",
  }, queryOptions);

  const worklistQuery = useIssuesQuery({
    ...scope,
    pageSize: TOP_ISSUES,
    statusGroup: "actionable",
    sort: "urgency",
  }, queryOptions);
  const topIssues = worklistQuery.data?.items ?? [];
  const actionableTotal = worklistQuery.data?.total ?? 0;

  const summary = summaryQuery.data;

  const lastRefreshedAt = useMemo<number | null>(() => {
    const stamps = [
      summaryQuery.dataUpdatedAt,
      issuesQuery.dataUpdatedAt,
      trendsQuery.dataUpdatedAt,
      worklistQuery.dataUpdatedAt,
    ].filter((n) => typeof n === "number" && n > 0);
    if (stamps.length === 0) return null;
    return Math.min(...stamps);
  }, [
    summaryQuery.dataUpdatedAt,
    issuesQuery.dataUpdatedAt,
    trendsQuery.dataUpdatedAt,
    worklistQuery.dataUpdatedAt,
  ]);

  const isAnyFetching =
    summaryQuery.isFetching ||
    issuesQuery.isFetching ||
    trendsQuery.isFetching ||
    worklistQuery.isFetching;

  function refreshAll(): void {
    void summaryQuery.refetch();
    void issuesQuery.refetch();
    void trendsQuery.refetch();
    void worklistQuery.refetch();
  }

  const greeting = getGreeting(new Date());
  const displayName = firstNameFromEmail(user?.email);

  return (
    <div className="flex flex-col gap-6">
      {/* Greeting + refresh */}
      <header className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {greeting}, {displayName}
          </h1>
          <p className="text-sm text-muted-foreground">
            Here&apos;s what&apos;s happening across your operations today.
          </p>
          <p className="text-xs text-muted-foreground">
            Showing <span className="text-foreground">{selectedStore?.name ?? "your accessible stores"}</span>
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {lastRefreshedAt ? (
            <span>
              Page refreshed {formatDistanceToNow(new Date(lastRefreshedAt), { addSuffix: true })}
            </span>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={refreshAll}
            disabled={isAnyFetching}
            aria-label="Refresh dashboard"
          >
            <RefreshCw className={cn("h-4 w-4", isAnyFetching && "animate-spin")} aria-hidden />
            Refresh
          </Button>
        </div>
      </header>

      {/* HERO — "how are we doing" beside "what do I fix". Everything below
          is context for these two. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <OperationsHealthCard
          points={trendsQuery.data?.points ?? []}
          isLoading={trendsQuery.isPending}
          errorDetail={
            trendsQuery.isError
              ? toErrorMessage(trendsQuery.error, "Couldn't load the detected gaps trend.")
              : null
          }
          emptyAction={
            <Button asChild size="sm" variant="outline" className="mt-1 w-fit">
              <Link to="/analytics/trends">Open Analytics trends</Link>
            </Button>
          }
        />
        <IssueTriageList
          issues={topIssues}
          actionableTotal={actionableTotal}
          viewAllTo="/compliance?statusGroup=actionable&sort=urgency"
          isLoading={worklistQuery.isPending}
          errorDetail={worklistQuery.isError ? "Couldn't load the worklist — try refreshing." : null}
          allClearHint="No actionable issues recorded in this scope. Check scan coverage before concluding the shelves are clear."
        />
      </div>

      {/* Counts strip. Headline numbers only — the breakdowns below own shape,
          not repetition. */}
      {summaryQuery.error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t load summary</AlertTitle>
          <AlertDescription>{toErrorMessage(summaryQuery.error, "Please try again.")}</AlertDescription>
        </Alert>
      ) : summaryQuery.isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <KpiCard
            icon={AlertTriangle}
            label="Critical issues"
            value={summary ? summary.issues.critical : 0}
            hint="Highest severity, unresolved"
            info="Issues marked critical that nobody has resolved yet. These are the ones worth interrupting a shift for."
            accentClassName="bg-danger/10 text-danger"
            to="/compliance"
          />
          <KpiCard
            icon={ClipboardList}
            label="Pending tasks"
            value={summary ? summary.tasks.pending : 0}
            hint={summary ? `${summary.tasks.submitted.toLocaleString()} awaiting review` : "—"}
            info="Open, assigned, in-progress and rejected tasks. Submitted tasks are tracked separately while awaiting review."
            accentClassName="bg-info/10 text-info"
            to="/compliance/store-tasks"
          />
          <KpiCard
            icon={Camera}
            label="Scans this week"
            value={summary ? summary.sessions.total_last_7d : 0}
            hint={summary ? `${summary.sessions.total_last_24h.toLocaleString()} in the last 24h` : "—"}
            info="Capture sessions started in the last 7 days. Scan counts describe activity, not coverage of every shelf."
            accentClassName="bg-info/10 text-info"
            to="/scans"
          />
        </div>
      )}

      {/* Context row — what kind of problems, and whether the catalog is
          holding recognition back. */}
      <div className="grid grid-cols-1 gap-4">
        <BreakdownCard
          title="What's going wrong most"
          subtitle="Actionable issues grouped by type"
          loading={issuesQuery.isLoading}
          error={issuesQuery.error}
          onRetry={() => void issuesQuery.refetch()}
          empty={!!issuesQuery.data && Object.keys(issuesQuery.data.actionableByType).length === 0}
          emptyMessage="No issues recorded yet."
        >
          {issuesQuery.data ? (
            <DistributionBars entries={recordToEntries(issuesQuery.data.actionableByType)} limit={6} />
          ) : null}
        </BreakdownCard>

      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        {QUICK_ACTIONS.map(({ to, label, icon: Icon }, i) => (
          <Button key={to} asChild variant={i === 0 ? "default" : "outline"} size="sm">
            <Link to={to}>
              <Icon className="h-4 w-4" aria-hidden />
              {label}
            </Link>
          </Button>
        ))}
      </div>
    </div>
  );
}
